import { useState } from 'react'
import { authService } from '../../services/authService'
import { useTranslation } from '../../contexts/TranslationContext'
import SectionCard from '../ui/SectionCard'
import FormField from '../ui/FormField'
import Input from '../ui/Input'
import Button from '../ui/Button'
import { ui } from '../../styles/ui'

export default function ChangePasswordCard() {
  const { t } = useTranslation()
  const [form, setForm] = useState({
    currentPassword: '',
    password: '',
    passwordConfirmation: '',
  })
  const [validationErrors, setValidationErrors] = useState({})
  const [errorMessage, setErrorMessage] = useState('')
  const [successMessage, setSuccessMessage] = useState('')
  const [saving, setSaving] = useState(false)

  const setField = (field, value) => {
    setForm((prev) => ({ ...prev, [field]: value }))
    setValidationErrors((prev) => ({ ...prev, [field]: '', form: '' }))
    setErrorMessage('')
    setSuccessMessage('')
  }

  const validate = () => {
    const errors = {}

    if (!form.currentPassword) {
      errors.currentPassword = t('Current password is required', 'Current password is required')
    }
    if (!form.password) {
      errors.password = t('New password is required', 'New password is required')
    } else if (form.password.length < 8) {
      errors.password = t('Password must be at least 8 characters', 'Password must be at least 8 characters')
    }
    if (!form.passwordConfirmation) {
      errors.passwordConfirmation = t('Please confirm your new password', 'Please confirm your new password')
    } else if (form.password !== form.passwordConfirmation) {
      errors.passwordConfirmation = t('Passwords do not match', 'Passwords do not match')
    }

    return errors
  }

  const handleSubmit = async (event) => {
    event.preventDefault()
    const errors = validate()
    setValidationErrors(errors)
    if (Object.keys(errors).length > 0) {
      return
    }

    setSaving(true)
    try {
      await authService.changePassword(form.currentPassword, form.password, form.passwordConfirmation)
      setForm({
        currentPassword: '',
        password: '',
        passwordConfirmation: '',
      })
      setSuccessMessage(t('Password changed successfully', 'Password changed successfully'))
      setErrorMessage('')
      setValidationErrors({})
    } catch (error) {
      const message = error.response?.data?.message || error.message || t('Failed to change password', 'Failed to change password')
      setErrorMessage(message)
      setSuccessMessage('')
    } finally {
      setSaving(false)
    }
  }

  return (
    <SectionCard
      title={t('Change Password', 'Change Password')}
      subtitle={t('Use your current password to set a new one.', 'Use your current password to set a new one.')}
    >
      <form onSubmit={handleSubmit} className="space-y-4">
        {successMessage ? (
          <div className={ui.alertInfo}>{successMessage}</div>
        ) : null}
        {errorMessage ? (
          <div className={ui.alertError}>{errorMessage}</div>
        ) : null}

        <FormField
          label={t('Current password', 'Current password')}
          htmlFor="change-password-current"
          error={validationErrors.currentPassword}
        >
          <Input
            id="change-password-current"
            type="password"
            autoComplete="current-password"
            value={form.currentPassword}
            onChange={(event) => setField('currentPassword', event.target.value)}
            aria-invalid={Boolean(validationErrors.currentPassword)}
          />
        </FormField>

        <FormField
          label={t('New password', 'New password')}
          htmlFor="change-password-new"
          error={validationErrors.password}
          helpText={t('Must be at least 8 characters.', 'Must be at least 8 characters.')}
        >
          <Input
            id="change-password-new"
            type="password"
            autoComplete="new-password"
            value={form.password}
            onChange={(event) => setField('password', event.target.value)}
            aria-invalid={Boolean(validationErrors.password)}
          />
        </FormField>

        <FormField
          label={t('Confirm new password', 'Confirm new password')}
          htmlFor="change-password-confirmation"
          error={validationErrors.passwordConfirmation}
        >
          <Input
            id="change-password-confirmation"
            type="password"
            autoComplete="new-password"
            value={form.passwordConfirmation}
            onChange={(event) => setField('passwordConfirmation', event.target.value)}
            aria-invalid={Boolean(validationErrors.passwordConfirmation)}
          />
        </FormField>

        <div className={ui.forms.actions}>
          <Button type="submit" variant="primary" disabled={saving}>
            {saving ? t('Saving...', 'Saving...') : t('Change Password', 'Change Password')}
          </Button>
        </div>
      </form>
    </SectionCard>
  )
}
